import React, { useState, useRef, useEffect } from 'react';

const TemplateTextField = ({
  fieldKey,
  label,
  value,
  onChange,
  placeholder,
  rows = 3,
  disabled,
  hint,
}) => {
  const [templates, setTemplates] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showSave, setShowSave] = useState(false);
  const [saveLabel, setSaveLabel] = useState('');
  const [saveAsDefault, setSaveAsDefault] = useState(false);
  const wrapperRef = useRef(null);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setIsOpen(false);
        setShowSave(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchTemplates = async () => {
    if (isLoading) return;
    setIsLoading(true);
    try {
      const { soaBuilderAPI } = await import('../../../services/api');
      const data = await soaBuilderAPI.listTemplates(fieldKey);
      setTemplates(data);
    } catch (err) {
      console.error('Failed to fetch templates:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleOpenPicker = async () => {
    if (isOpen) {
      setIsOpen(false);
      return;
    }
    await fetchTemplates();
    setIsOpen(true);
    setShowSave(false);
  };

  const handleSelectTemplate = (template) => {
    onChange(template.content);
    setIsOpen(false);
  };

  const handleSaveAsTemplate = async () => {
    if (!saveLabel.trim() || !value.trim()) return;
    try {
      const { soaBuilderAPI } = await import('../../../services/api');
      await soaBuilderAPI.createTemplate({
        field_key: fieldKey,
        label: saveLabel.trim(),
        content: value,
        is_default: saveAsDefault,
      });
      setShowSave(false);
      setSaveLabel('');
      setSaveAsDefault(false);
      // Refresh list
      await fetchTemplates();
    } catch (err) {
      console.error('Failed to save template:', err);
    }
  };

  const handleToggleDefault = async (template) => {
    try {
      const { soaBuilderAPI } = await import('../../../services/api');
      const updated = await soaBuilderAPI.setTemplateDefault(template.id);
      setTemplates(prev => prev.map(t =>
        t.id === template.id
          ? { ...t, is_default: updated.is_default }
          : t.field_key === template.field_key
            ? { ...t, is_default: false }
            : t
      ));
    } catch (err) {
      console.error('Failed to toggle default:', err);
    }
  };

  const handleDeleteTemplate = async (template) => {
    try {
      const { soaBuilderAPI } = await import('../../../services/api');
      await soaBuilderAPI.deleteTemplate(template.id);
      setTemplates(prev => prev.filter(t => t.id !== template.id));
    } catch (err) {
      console.error('Failed to delete template:', err);
    }
  };

  return (
    <div className="soa-form-group" ref={wrapperRef} style={{ position: 'relative' }}>
      <div className="d-flex align-items-center justify-content-between">
        <label>{label}</label>
        <button
          type="button"
          className="btn btn-sm p-0 soa-template-btn"
          onClick={handleOpenPicker}
          disabled={disabled}
          title="Templates"
          style={{ color: isOpen ? 'var(--secondary-color)' : 'var(--gray-color)', border: 'none', background: 'none' }}
        >
          <i className={`bi ${isOpen ? 'bi-bookmark-fill' : 'bi-bookmark'}`} style={{ fontSize: '1rem' }}></i>
        </button>
      </div>

      <textarea
        className="form-control"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        rows={rows}
        disabled={disabled}
      />
      {hint && <small className="text-muted">{hint}</small>}

      {isOpen && (
        <div className="soa-template-dropdown">
          {isLoading && (
            <div className="text-center p-2">
              <span className="spinner-border spinner-border-sm text-muted"></span>
            </div>
          )}

          {!isLoading && templates.length > 0 && (
            <div className="soa-template-list">
              {templates.map((t) => (
                <div key={t.id} className="soa-template-item">
                  <button
                    type="button"
                    className="soa-template-item-select"
                    onClick={() => handleSelectTemplate(t)}
                  >
                    <span className="fw-semibold">{t.label}</span>
                    <span className="text-muted small d-block" style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '280px' }}>
                      {t.content.substring(0, 80)}{t.content.length > 80 ? '...' : ''}
                    </span>
                  </button>
                  <div className="soa-template-item-actions">
                    <button
                      type="button"
                      title={t.is_default ? 'Remove as default' : 'Set as default'}
                      onClick={() => handleToggleDefault(t)}
                      style={{ color: t.is_default ? 'var(--secondary-color)' : 'var(--gray-color)' }}
                    >
                      <i className={`bi ${t.is_default ? 'bi-star-fill' : 'bi-star'}`}></i>
                    </button>
                    <button
                      type="button"
                      title="Delete template"
                      onClick={() => handleDeleteTemplate(t)}
                      style={{ color: '#dc3545' }}
                    >
                      <i className="bi bi-trash"></i>
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {!isLoading && templates.length === 0 && !showSave && (
            <div className="text-muted small p-2 text-center">No templates saved yet</div>
          )}

          {/* Save current text as template */}
          {!showSave ? (
            <button
              type="button"
              className="soa-template-save-trigger"
              onClick={() => setShowSave(true)}
              disabled={!value?.trim()}
            >
              <i className="bi bi-plus-circle me-1"></i>Save current text as template
            </button>
          ) : (
            <div className="p-2 border-top" style={{ backgroundColor: '#f8f9fb' }}>
              <input
                type="text"
                className="form-control form-control-sm mb-1"
                placeholder="Template label..."
                value={saveLabel}
                onChange={(e) => setSaveLabel(e.target.value)}
                autoFocus
              />
              <div className="d-flex align-items-center justify-content-between">
                <div className="form-check form-check-sm">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id={`default-${fieldKey}`}
                    checked={saveAsDefault}
                    onChange={(e) => setSaveAsDefault(e.target.checked)}
                  />
                  <label className="form-check-label small" htmlFor={`default-${fieldKey}`}>
                    Set as default
                  </label>
                </div>
                <div className="d-flex gap-1">
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-secondary"
                    onClick={() => { setShowSave(false); setSaveLabel(''); }}
                    style={{ fontSize: '0.75rem', padding: '2px 8px' }}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="btn btn-sm text-white"
                    onClick={handleSaveAsTemplate}
                    disabled={!saveLabel.trim()}
                    style={{ fontSize: '0.75rem', padding: '2px 8px', backgroundColor: 'var(--primary-color)' }}
                  >
                    Save
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default TemplateTextField;
